import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import { toast } from 'sonner';
import { SubsidyReportsService } from '@/api';
import type {
  subsidy_CreateSubsidyReportRequest,
  subsidy_FileSubsidyReportRequest,
  subsidy_RecordSubsidyReportDecisionRequest,
} from '@/api';
import { errorToastText } from '@/lib/translateBackendError';
import { contractKeys } from '@/hooks/useContracts';
import { workflowKeys } from '@/hooks/useWorkflows';
import type { SubsidyReportItem, SubsidyReportSheet } from '@/types/subsidyReport';

// Sozialdepartement work queue (#2003). The items are workflow items, so every
// write also invalidates the inbox; the decision and the filing write the
// contract version, so the contract caches go too.
export const subsidyReportKeys = {
  all: ['subsidy-reports'] as const,
  sheet: (orgId: string | null, itemId: string) => [...subsidyReportKeys.all, 'sheet', orgId, itemId] as const,
};

export function useSubsidyReportSheet(itemId: string | null | undefined) {
  const { orgId } = useAuth();
  return useQuery<SubsidyReportSheet>({
    queryKey: itemId ? subsidyReportKeys.sheet(orgId ?? null, itemId) : [...subsidyReportKeys.all, 'none'],
    queryFn: () => SubsidyReportsService.getApiV1SubsidyReportsSheet(itemId as string),
    enabled: !!itemId && !!orgId,
  });
}

function useInvalidateAfterWrite() {
  const queryClient = useQueryClient();
  return async (itemId?: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: subsidyReportKeys.all }),
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: workflowKeys.pendingCount() }),
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() }),
      itemId ? queryClient.invalidateQueries({ queryKey: workflowKeys.detail(itemId) }) : Promise.resolve(),
      queryClient.invalidateQueries({ queryKey: contractKeys.all }),
    ]);
  };
}

export function useFileSubsidyReport(itemId: string) {
  const invalidate = useInvalidateAfterWrite();
  return useMutation<SubsidyReportItem, unknown, subsidy_FileSubsidyReportRequest>({
    mutationFn: (request) => SubsidyReportsService.postApiV1SubsidyReportsFile(itemId, request),
    onSuccess: async () => {
      await invalidate(itemId);
    },
    meta: {
      successMessage: msg({ id: 'subsidyReport.file.success', message: 'Marked as filed' }),
      errorFallback: msg({ id: 'subsidyReport.file.error', message: 'Could not mark it filed. Please try again.' }),
    },
  });
}

export function useRecordSubsidyReportDecision(itemId: string) {
  const invalidate = useInvalidateAfterWrite();
  return useMutation<SubsidyReportItem, unknown, subsidy_RecordSubsidyReportDecisionRequest>({
    mutationFn: (request) => SubsidyReportsService.postApiV1SubsidyReportsDecision(itemId, request),
    onSuccess: async () => {
      await invalidate(itemId);
    },
    meta: {
      successMessage: msg({ id: 'subsidyReport.decision.success', message: 'Decision recorded' }),
      errorFallback: msg({ id: 'subsidyReport.decision.error', message: 'Could not record the decision. Please try again.' }),
    },
  });
}

export function useCreateSubsidyReport(contractId: string) {
  const invalidate = useInvalidateAfterWrite();
  return useMutation<SubsidyReportItem, unknown, subsidy_CreateSubsidyReportRequest>({
    mutationFn: (request) => SubsidyReportsService.postApiV1ContractsSubsidyReports(contractId, request),
    onSuccess: async () => {
      await invalidate();
    },
    meta: {
      successMessage: msg({ id: 'subsidyReport.create.success', message: 'Item added to the Sozialdepartement queue' }),
      errorFallback: msg({ id: 'subsidyReport.create.error', message: 'Could not add the item. Please try again.' }),
    },
  });
}

export interface SubsidyReportExportParams {
  from?: string;
  to?: string;
  state?: string;
}

type ExportError = Error & { status: number; body?: unknown };

/** The CSV streams as text/csv, so it uses a raw fetch with a bearer token (like the dunning letter). */
export async function downloadSubsidyReportExport(params: SubsidyReportExportParams): Promise<{ blob: Blob; filename: string }> {
  const { getAuthToken } = await import('@/api/auth-helper');
  const token = await getAuthToken();
  const baseUrl = import.meta.env.VITE_API_BASE || '';
  const query = new URLSearchParams();
  if (params.from) query.set('from', params.from);
  if (params.to) query.set('to', params.to);
  if (params.state) query.set('state', params.state);
  const suffix = query.toString() ? `?${query.toString()}` : '';

  const response = await fetch(`${baseUrl}/api/v1/subsidy-reports/export.csv${suffix}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw Object.assign(new Error(`HTTP ${response.status}`), { status: response.status, body }) as ExportError;
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
  return { blob, filename: filenameMatch?.[1] || 'sozialdepartement.csv' };
}

export function useDownloadSubsidyReportExport() {
  const { i18n } = useLingui();
  return useMutation<{ blob: Blob; filename: string }, ExportError, SubsidyReportExportParams>({
    mutationFn: downloadSubsidyReportExport,
    onSuccess: ({ blob, filename }) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
    onError: (error) => {
      toast.error(
        errorToastText(
          error,
          i18n,
          msg({ id: 'subsidyReport.export.error', message: 'The export could not be created. Please try again.' }),
        ),
      );
    },
  });
}
